import { NetworkStatus, useMutation, useQuery } from '@apollo/client';
import { TextField } from '@mui/material';
import { gql } from '__generated__';
import { UserFilterInputType, UserStatusEnumType, UserType } from '__generated__/graphql';
import { FC } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import Button from 'components/Button';
import DataTable from 'components/DataTable';
import ErrorMessage from 'components/ErrorMessage';
import FilterBar from 'components/FilterBar';
import LoadingIndicator from 'components/LoadingIndicator';

import debounce from 'utils/debounce';
import { formatNumber } from 'utils/format-helper';
import statusEnumToReadable from 'utils/status-enum-to-readable';

import BulkUploadUser from './BulkUploadUser';
import GenerateIdentityCardsForDepartment from './GenerateIdentityCardsForDepartment';
import GenerateUserReportForDepartment from './GenerateUserReportForDepartment';

type UserStats = { numberOfUsers: number; status: 'INACTIVE' | 'ACTIVE' };
type EmployeeStats = { numberOfEmployees: number; organisationCode: 'DOE' | 'TSC' | 'OLA' | 'MOE' };

const DEPARTMENT_QUERY = gql(`
  query DepartmentUsers(
    $departmentId: ID!
    $limit: Int
    $cursor: ID
    $filters: UserFilterInputType
  ) {
    department(id: $departmentId) {
      id
      users(limit: $limit, cursor: $cursor, filters: $filters) {
        nodes {
          id
          fullName
          status
          createdAt
          employee {
            id
            fileNumber
            organisationCode
            photo
            signature
            designation {
              name
            }
            division {
              name
            }
            address {
              city
            }
          }
        }
        pageInfo {
          cursor
          totalCount
          hasNextPage
        }
        statistics {
          employeeStatisticsBreakdownByOrganisationCode(departmentId: $departmentId) {
            numberOfEmployees
            organisationCode
          }
          userStatisticsBreakdownByStatus(departmentId: $departmentId) {
            numberOfUsers
            status
          }
        }
      }
    }
  }
`);

const UPDATE_BULK_USER_STATUS = gql(`
  mutation UpdateBulkUserStatus($userIds: [ID!]!, $status: UserStatusEnumType!) {
    updateBulkUserStatus(ids: $userIds, status: $status)
  }
`);

const GENERATE_IDENTITY_CARDS_MUTATION = gql(`
  mutation GenerateIdentityCardsForDepartment($departmentId: ID!, $userIds: [ID!]) {
    generateIdentityCardsForDepartment(departmentId: $departmentId, userIds: $userIds)
  }
`);

const DepartmentUsers: FC = () => {
  const navigate = useNavigate();
  const { departmentId } = useParams<{ departmentId: string }>() as { departmentId: string };

  const { loading, error, data, refetch, fetchMore, variables, networkStatus } = useQuery(
    DEPARTMENT_QUERY,
    {
      variables: {
        departmentId,
        limit: 30,
        filters: {
          status: UserStatusEnumType.Active,
        } as UserFilterInputType,
      },
      notifyOnNetworkStatusChange: true,
    }
  );

  const [generateIdentityCards, { loading: generateLoading, error: generateError }] = useMutation(
    GENERATE_IDENTITY_CARDS_MUTATION,
    {
      refetchQueries: ['DepartmentIdentityCards'],
      onCompleted() {
        navigate(`/departments/${departmentId}/identity-cards`);
      },
    }
  );

  const [
    updateBulkUserStatus,
    { loading: updateBulkUserStatusLoading, error: updateBulkUserStatusError },
  ] = useMutation(UPDATE_BULK_USER_STATUS, {
    update(cache, { data }, context) {
      // Get IDs from the variables
      const updatedUserIds = context.variables?.userIds;
      const updatedStatus = context.variables?.status;

      if (!data || !data.updateBulkUserStatus || !updatedUserIds) return;

      Array.isArray(updatedUserIds) &&
        updatedUserIds.forEach((userId: string) => {
          cache.modify({
            id: `UserType:${userId}`,
            fields: {
              status() {
                return updatedStatus;
              },
            },
          });
        });
    },
  });

  const getOrganisationCodeFilterWithLabels = (
    statistics: {
      numberOfEmployees: number;
      organisationCode: 'DOE' | 'TSC' | 'OLA' | 'MOE';
    }[]
  ) => {
    return [
      {
        label: 'All',
        value: undefined,
      },
      ...statistics.map(entry => ({
        label: `${entry.organisationCode} (${formatNumber(entry.numberOfEmployees)})`,
        value: entry.organisationCode,
      })),
    ];
  };

  const getStatusFilterWithLabels = (
    statistics: {
      numberOfUsers: number;
      status: 'INACTIVE' | 'ACTIVE';
    }[]
  ) => {
    const allStatuses = Object.values(UserStatusEnumType);

    return [
      {
        label: 'All',
        value: undefined,
      },
      ...allStatuses.map(status => {
        const stat = statistics.find(s => s.status === status);
        return {
          label: `${statusEnumToReadable(status).label} (${stat?.numberOfUsers ?? 0})`,
          value: status,
        };
      }),
    ];
  };

  if (loading && !data) return <LoadingIndicator />;

  if (error || !data) return <ErrorMessage error={error} refetch={refetch} />;

  const users = data.department.users.nodes;
  const statistics = data.department.users.statistics;
  const pageInfo = data.department.users.pageInfo;

  return (
    <>
      <div style={{ display: 'flex', gap: '5px' }}>
        <Button onClick={() => navigate(`/create/departments/${departmentId}/user`)}>
          Create User
        </Button>{' '}
        <GenerateIdentityCardsForDepartment departmentId={departmentId} />{' '}
        <GenerateUserReportForDepartment departmentId={departmentId} />{' '}
        <BulkUploadUser departmentId={departmentId} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 0 }}>
        <div>
          {departmentId === '20009c7b-5c40-4d60-b3cc-9ea4c801112e' && (
            <FilterBar
              title="Organisation Code"
              filters={getOrganisationCodeFilterWithLabels(
                statistics.employeeStatisticsBreakdownByOrganisationCode as EmployeeStats[]
              )}
              currentValue={variables?.filters?.organisationCode as string}
              onSelect={value => {
                refetch({
                  filters: {
                    organisationCode: value as 'DOE' | 'TSC' | 'OLA' | 'MOE',
                  } as UserFilterInputType,
                });
              }}
            />
          )}
          <FilterBar
            title="Status"
            filters={getStatusFilterWithLabels(
              statistics.userStatisticsBreakdownByStatus as UserStats[]
            )}
            currentValue={variables?.filters?.status as UserStatusEnumType}
            onSelect={value => {
              refetch({
                filters: {
                  status: value as UserStatusEnumType,
                },
              });
            }}
          />
        </div>
        <TextField
          placeholder="Name, File number or Designation"
          size="small"
          onChange={debounce(e => {
            refetch({
              filters: {
                ...variables?.filters,
                text: e.target.value,
              },
            });
          }, 500)}
        />
      </div>
      <DataTable
        data={users as UserType[]}
        onClick={user => {
          user.employee
            ? navigate(`/users/${user.id}`)
            : navigate(`/create/departments/${departmentId}/user/${user.id}/employee-information`);
        }}
        bulkSelectActions={[
          {
            label: 'Generate ID Cards',
            loading: generateLoading,
            error: generateError,
            action: async selectedIds => {
              generateIdentityCards({
                variables: {
                  departmentId,
                  userIds: selectedIds,
                },
              });
            },
          },
          {
            label: 'Set as Active',
            loading: updateBulkUserStatusLoading,
            error: updateBulkUserStatusError,
            action: async selectedIds => {
              updateBulkUserStatus({
                variables: {
                  userIds: selectedIds,
                  status: UserStatusEnumType.Active,
                },
              });
            },
          },
          {
            label: 'Set as Inactive',
            loading: updateBulkUserStatusLoading,
            error: updateBulkUserStatusError,
            action: async selectedIds => {
              updateBulkUserStatus({
                variables: {
                  userIds: selectedIds,
                  status: UserStatusEnumType.Inactive,
                },
              });
            },
          },
        ]}
        columns={[
          {
            label: 'ID',
            fieldName: 'id',
            type: 'SELECT',
          },
          {
            label: 'Full Name',
            fieldName: 'fullName',
          },
          {
            label: 'File Number',
            fieldName: 'employee.fileNumber',
          },
          ...(departmentId === '20009c7b-5c40-4d60-b3cc-9ea4c801112e'
            ? [
                {
                  label: 'Organisation',
                  fieldName: 'employee.organisationCode',
                },
              ]
            : []),
          {
            label: 'Photo',
            fieldName: 'employee.photo',
            type: 'IMAGE',
          },
          {
            label: 'Signature',
            fieldName: 'employee.signature',
            type: 'IMAGE',
          },
          {
            label: 'Designation',
            fieldName: 'employee.designation.name',
          },
          {
            label: 'Division',
            fieldName: 'employee.division.name',
          },
          {
            label: 'City',
            fieldName: 'employee.address.city',
          },
          {
            label: 'Created At',
            fieldName: 'createdAt',
            type: 'DATETIME',
          },
          {
            label: 'Status',
            fieldName: 'status',
            type: 'STATUS',
          },
        ]}
        hasNextPage={pageInfo.hasNextPage as boolean}
        paginationLoading={loading}
        filterLoading={networkStatus === NetworkStatus.setVariables}
        onLoadMore={() =>
          fetchMore({
            variables: {
              cursor: pageInfo.cursor,
            },
          })
        }
        totalCount={pageInfo.totalCount as number}
      />
    </>
  );
};

export default DepartmentUsers;
